import React, { useState } from 'react';
import { toast } from 'sonner';
import { Gift, Search, Plus, History, User, Heart, Video, Phone, Calendar, Sparkles, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { apiClient } from '../../lib/api-client';
import { Badge } from '../ui/badge';
import { logger } from '../../utils/logger';

interface RewardHistoryItem {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  rewardType: string;
  amount: number;
  reason: string;
  grantedBy: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface UserProfile {
  _id: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    subscription?: {
      plan?: string;
      endDate?: string;
      features?: {
        dailyContactViewLimit?: number;
        dailyInterestLimit?: number;
        dailyVideoCallLimit?: number;
        totalSuperInterests?: number;
      };
    };
  };
  contactViewsRemaining?: number;
  dailyInterestsRemaining?: number;
  videoCallsRemaining?: number;
  totalSuperInterestsUsed?: number;
}

export function AdminRewards() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [rewardHistory, setRewardHistory] = useState<RewardHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [grantingRewards, setGrantingRewards] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRewards, setTotalRewards] = useState(0);
  const [activeTab, setActiveTab] = useState('grant');
  const itemsPerPage = 10;

  const [rewardForm, setRewardForm] = useState({
    contactViews: 0,
    interests: 0,
    videoCallCredits: 0,
    superInterests: 0,
    reason: ''
  });

  const handleSearchUser = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a user ID, email, or phone number');
      return;
    }

    try {
      setSearchLoading(true);
      logger.log('🔍 Searching user:', searchQuery);

      const response = await apiClient.get(`/admin/users/search?query=${encodeURIComponent(searchQuery)}`);
      
      if (response.data.success && response.data.data) {
        setSelectedUser(response.data.data);
        toast.success('User found!');
        logger.log('✅ User found:', response.data.data);
      } else {
        toast.error('User not found');
        setSelectedUser(null);
      }
    } catch (error: any) {
      logger.error('❌ Error searching user:', error);
      toast.error(error.response?.data?.message || 'Failed to search user');
      setSelectedUser(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleGrantRewards = async () => {
    if (!selectedUser) {
      toast.error('Please select a user first');
      return;
    }

    const hasAnyReward = 
      rewardForm.contactViews > 0 ||
      rewardForm.interests > 0 ||
      rewardForm.videoCallCredits > 0 ||
      rewardForm.superInterests > 0;

    if (!hasAnyReward) {
      toast.error('Please add at least one reward');
      return;
    }

    try {
      setGrantingRewards(true);
      const payload = {
        userId: selectedUser.userId._id,
        rewards: {
          contactViews: rewardForm.contactViews,
          interests: rewardForm.interests,
          videoCallCredits: rewardForm.videoCallCredits,
          superInterests: rewardForm.superInterests
        },
        reason: rewardForm.reason
      };

      logger.log('🎁 Granting rewards payload:', payload);

      const response = await apiClient.post('/admin/rewards/grant', payload);

      if (response.data.success) {
        toast.success('Rewards granted successfully! 🎉');
        
        // Reset form
        setRewardForm({
          contactViews: 0,
          interests: 0,
          videoCallCredits: 0,
          superInterests: 0,
          reason: ''
        });
        
        // Close dialog
        setIsDialogOpen(false);
        
        // Refresh user data
        handleSearchUser();
        
        // Refresh history
        fetchRewardHistory();
      }
    } catch (error: any) {
      logger.error('❌ Error granting rewards:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to grant rewards';
      toast.error(errorMessage);
      
      // Log full error details for debugging
      logger.error('Full error response:', error.response?.data);
    } finally {
      setGrantingRewards(false);
    }
  };

  const fetchRewardHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await apiClient.get('/admin/rewards/history?limit=50');
      
      if (response.data.success) {
        setRewardHistory(response.data.data.rewards || []);
        setTotalRewards(response.data.data.total || 0);
        setTotalPages(Math.ceil(response.data.data.total / itemsPerPage));
      }
    } catch (error: any) {
      logger.error('❌ Error fetching reward history:', error);
      // Don't show error toast if endpoint doesn't exist yet
      if (error.response?.status !== 404) {
        toast.error('Failed to load reward history');
      }
      setRewardHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  React.useEffect(() => {
    // Don't fetch on mount, wait for backend to be ready
    // fetchRewardHistory();
  }, []);

  // Fetch reward history when history tab is opened
  React.useEffect(() => {
    if (activeTab === 'history') {
      fetchRewardHistory();
    }
  }, [activeTab]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRewardTypeIcon = (type: string) => {
    switch (type) {
      case 'contactViews':
        return <Eye className="size-4 text-blue-600" />;
      case 'interests':
        return <Heart className="size-4 text-pink-600" />;
      case 'videoCallCredits':
        return <Video className="size-4 text-purple-600" />;
      case 'superInterests':
        return <Sparkles className="size-4 text-amber-600" />;
      default:
        return <Gift className="size-4 text-gray-600" />;
    }
  };

  const getRewardTypeName = (type: string) => {
    switch (type) {
      case 'contactViews':
        return 'Contact Views';
      case 'interests':
        return 'Interest Credits';
      case 'videoCallCredits':
        return 'Video Call Credits';
      case 'superInterests':
        return 'Super Interest Credits';
      default:
        return type;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gift className="size-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">User Rewards</h1>
        </div>
      </div>

      <Tabs defaultValue="grant" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="grant">Grant Rewards</TabsTrigger>
          <TabsTrigger value="history">Reward History</TabsTrigger>
        </TabsList>

        {/* Grant Rewards Tab */}
        <TabsContent value="grant" className="space-y-6">
          {/* Search User */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="size-5" />
                Search User
              </CardTitle>
              <CardDescription>
                Search by User ID, Email, or Phone Number
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter User ID, Email, or Phone Number"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearchUser();
                    }
                  }}
                />
                <Button onClick={handleSearchUser} disabled={searchLoading}>
                  {searchLoading ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {/* Selected User Info */}
              {selectedUser && selectedUser.userId && (
                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-600 text-white rounded-full size-12 flex items-center justify-center">
                          <User className="size-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {selectedUser.userId.firstName} {selectedUser.userId.lastName}
                          </h3>
                          <div className="space-y-1 mt-1">
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="font-medium">Email:</span> 
                              <span className="font-mono bg-white px-2 py-0.5 rounded">{selectedUser.userId.email}</span>
                            </p>
                            <p className="text-sm text-gray-600 flex items-center gap-2">
                              <span className="font-medium">Phone:</span> 
                              <span className="font-mono bg-white px-2 py-0.5 rounded">{selectedUser.userId.phone}</span>
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-2">
                              <span className="font-medium">User ID:</span> 
                              <span className="font-mono bg-white px-2 py-0.5 rounded">{selectedUser.userId._id}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogTrigger asChild>
                          <Button className="bg-purple-600 hover:bg-purple-700">
                            <Gift className="size-4 mr-2" />
                            Grant Rewards
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Grant Rewards to {selectedUser.userId.firstName}</DialogTitle>
                            <DialogDescription>
                              Add credits and rewards to the user's account
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            {/* Contact Views */}
                            <div className="space-y-2">
                              <Label htmlFor="contactViews" className="flex items-center gap-2">
                                <Eye className="size-4 text-blue-600" />
                                Contact View Credits
                              </Label>
                              <Input
                                id="contactViews"
                                type="number"
                                min="0"
                                value={rewardForm.contactViews}
                                onChange={(e) => setRewardForm({ ...rewardForm, contactViews: Number(e.target.value) })}
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500">
                                Daily contact view credits (renews daily)
                              </p>
                            </div>

                            {/* Interest Credits */}
                            <div className="space-y-2">
                              <Label htmlFor="interests" className="flex items-center gap-2">
                                <Heart className="size-4 text-pink-600" />
                                Interest Credits
                              </Label>
                              <Input
                                id="interests"
                                type="number"
                                min="0"
                                value={rewardForm.interests}
                                onChange={(e) => setRewardForm({ ...rewardForm, interests: Number(e.target.value) })}
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500">
                                Daily interest sending credits (renews daily)
                              </p>
                            </div>

                            {/* Video Call Credits */}
                            <div className="space-y-2">
                              <Label htmlFor="videoCallCredits" className="flex items-center gap-2">
                                <Video className="size-4 text-purple-600" />
                                Video Call Credits
                              </Label>
                              <Input
                                id="videoCallCredits"
                                type="number"
                                min="0"
                                value={rewardForm.videoCallCredits}
                                onChange={(e) => setRewardForm({ ...rewardForm, videoCallCredits: Number(e.target.value) })}
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500">
                                Daily video call credits (renews daily)
                              </p>
                            </div>

                            {/* Super Interest Credits */}
                            <div className="space-y-2">
                              <Label htmlFor="superInterests" className="flex items-center gap-2">
                                <Sparkles className="size-4 text-amber-600" />
                                Super Interest Credits
                              </Label>
                              <Input
                                id="superInterests"
                                type="number"
                                min="0"
                                value={rewardForm.superInterests}
                                onChange={(e) => setRewardForm({ ...rewardForm, superInterests: Number(e.target.value) })}
                                placeholder="0"
                              />
                              <p className="text-xs text-gray-500">
                                One-time super interest credits (not daily)
                              </p>
                            </div>

                            {/* Reason */}
                            <div className="space-y-2">
                              <Label htmlFor="reason" className="flex items-center gap-2">
                                <Gift className="size-4 text-gray-600" />
                                Reason
                              </Label>
                              <Textarea
                                id="reason"
                                value={rewardForm.reason}
                                onChange={(e) => setRewardForm({ ...rewardForm, reason: e.target.value })}
                                placeholder="Enter the reason for granting rewards"
                              />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-2 pt-4">
                              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={grantingRewards}>
                                Cancel
                              </Button>
                              <Button onClick={handleGrantRewards} className="bg-purple-600 hover:bg-purple-700" disabled={grantingRewards}>
                                {grantingRewards ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                    Granting...
                                  </>
                                ) : (
                                  <>
                                    <Gift className="size-4 mr-2" />
                                    Grant Rewards
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Current Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Eye className="size-4 text-blue-600" />
                          <span className="text-xs text-gray-600">Contact Views</span>
                        </div>
                        <p className="text-lg font-semibold">
                          {selectedUser.userId.subscription?.features?.dailyContactViewLimit === -1 
                            ? '∞' 
                            : selectedUser.userId.subscription?.features?.dailyContactViewLimit || 0}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Heart className="size-4 text-pink-600" />
                          <span className="text-xs text-gray-600">Interests/Day</span>
                        </div>
                        <p className="text-lg font-semibold">
                          {selectedUser.userId.subscription?.features?.dailyInterestLimit === -1 
                            ? '∞' 
                            : selectedUser.userId.subscription?.features?.dailyInterestLimit || 0}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Video className="size-4 text-purple-600" />
                          <span className="text-xs text-gray-600">Video Calls/Day</span>
                        </div>
                        <p className="text-lg font-semibold">
                          {selectedUser.userId.subscription?.features?.dailyVideoCallLimit === -1 
                            ? '∞' 
                            : selectedUser.userId.subscription?.features?.dailyVideoCallLimit || 0}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="size-4 text-amber-600" />
                          <span className="text-xs text-gray-600">Super Interests</span>
                        </div>
                        <p className="text-lg font-semibold">
                          {(selectedUser.userId.subscription?.features?.totalSuperInterests || 0) - (selectedUser.totalSuperInterestsUsed || 0)}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="size-4 text-green-600" />
                          <span className="text-xs text-gray-600">Plan</span>
                        </div>
                        <p className="text-sm font-semibold">
                          {selectedUser.userId.subscription?.plan || 'Free'}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="size-4 text-green-600" />
                          <span className="text-xs text-gray-600">Valid Until</span>
                        </div>
                        <p className="text-xs font-semibold">
                          {selectedUser.userId.subscription?.endDate 
                            ? new Date(selectedUser.userId.subscription.endDate).toLocaleDateString('en-IN')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reward History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="size-5" />
                    Reward History
                  </CardTitle>
                  <CardDescription>
                    Recent rewards granted to users ({totalRewards} total)
                  </CardDescription>
                </div>
                <Button onClick={fetchRewardHistory} variant="outline" size="sm">
                  <History className="size-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading history...</p>
                  </div>
                </div>
              ) : rewardHistory.length === 0 ? (
                <div className="text-center py-12">
                  <Gift className="size-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No rewards yet</h3>
                  <p className="text-gray-500">Start granting rewards to users!</p>
                </div>
              ) : (
                <>
                  {/* Compact Table View */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-2">User</th>
                          <th className="text-left py-3 px-2">Reward Type</th>
                          <th className="text-center py-3 px-2">Amount</th>
                          <th className="text-left py-3 px-2">Reason</th>
                          <th className="text-left py-3 px-2">Granted By</th>
                          <th className="text-left py-3 px-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rewardHistory
                          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                          .map((reward) => (
                            <tr 
                              key={reward._id} 
                              className="border-b border-gray-100 hover:bg-purple-50/50 transition-colors"
                            >
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <div className="bg-purple-100 text-purple-600 rounded-full size-8 flex items-center justify-center flex-shrink-0">
                                    {getRewardTypeIcon(reward.rewardType)}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">
                                      {reward.userId.firstName} {reward.userId.lastName}
                                    </p>
                                    <p className="text-xs text-gray-500">{reward.userId.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <Badge className="bg-purple-600">
                                  {getRewardTypeName(reward.rewardType)}
                                </Badge>
                              </td>
                              <td className="py-3 px-2 text-center">
                                <Badge variant="outline" className="font-semibold">
                                  +{reward.amount}
                                </Badge>
                              </td>
                              <td className="py-3 px-2">
                                <p className="text-sm text-gray-600 line-clamp-2 max-w-xs">
                                  {reward.reason}
                                </p>
                              </td>
                              <td className="py-3 px-2">
                                <p className="text-sm text-gray-600">
                                  {reward.grantedBy.firstName} {reward.grantedBy.lastName}
                                </p>
                              </td>
                              <td className="py-3 px-2">
                                <p className="text-xs text-gray-500">
                                  {formatDate(reward.createdAt)}
                                </p>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRewards)} of {totalRewards} rewards
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="size-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              // Show first page, last page, current page, and adjacent pages
                              return page === 1 || 
                                     page === totalPages || 
                                     (page >= currentPage - 1 && page <= currentPage + 1);
                            })
                            .map((page, idx, arr) => (
                              <React.Fragment key={page}>
                                {idx > 0 && arr[idx - 1] !== page - 1 && (
                                  <span className="px-2 text-gray-400">...</span>
                                )}
                                <Button
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className={currentPage === page ? "bg-purple-600 hover:bg-purple-700" : ""}
                                >
                                  {page}
                                </Button>
                              </React.Fragment>
                            ))}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <ChevronRight className="size-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}